import * as React from "react";
import type { NavigateFunction } from "react-router-dom";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { error } from "app/themes/colors";
import type { ConfirmDialogOptions } from "components/confirm-dialog/confirm-dialog.component";
import {
  MANAGED_CARE_PAYMENT_SOURCE,
  PRIVATE_PAY_PAYMENT_SOURCE
} from "constants/patients";
import {
  GRAVITY_PAYMENT_METHOD,
  TILLED_PAYMENT_METHOD,
  resolveCardProcessingProvider
} from "constants/payments";
import {
  SALE_CANCELED_STATUS_TYPE,
  SALE_COMPLETED_STATUS_TYPE,
  SALE_DRAFT_STATUS_TYPE
} from "constants/sales";
import type {
  Clinic,
  PracticeBillingSettings
} from "services/types/clinics.type";
import { formatBackendDateOnly } from "utils/common";

type GetFileInsuranceClaimConfigProps = {
  sale: any;
  noInsurancePolicies?: boolean;
};

export const getFileInsuranceClaimConfig = ({
  sale: { status, items, payment_source_type },
  noInsurancePolicies
}: GetFileInsuranceClaimConfigProps) => {
  if (status === SALE_CANCELED_STATUS_TYPE) {
    return {
      disabled: true,
      tooltip: (
        <Typography>
          You cannot file an insurance claim for a cancelled sale
        </Typography>
      )
    };
  }

  if (
    !payment_source_type ||
    payment_source_type === MANAGED_CARE_PAYMENT_SOURCE ||
    payment_source_type === PRIVATE_PAY_PAYMENT_SOURCE
  ) {
    return {
      disabled: true,
      tooltip: <Typography>Please set payment source to insurance</Typography>
    };
  }

  const disabledByStatus =
    status === SALE_DRAFT_STATUS_TYPE || status === SALE_COMPLETED_STATUS_TYPE;

  const haveLineItemsWithCptCodes =
    (items || [])?.filter((item: any) => Boolean(item.cpt_code)).length > 0;

  const haveLineItemsWithInsuranceResponsibility =
    (items || [])?.filter(
      (item: any) => Number(item.insurer_responsibility) > 0
    ).length > 0;

  const disabledByList = [];

  if (disabledByStatus) {
    disabledByList.push(`Change status of sale to "Ready to bill" Status`);
  }

  if (!haveLineItemsWithCptCodes) {
    disabledByList.push(
      `Assign CPT Codes to Line items you want to upload on claim`
    );
  }

  if (!haveLineItemsWithInsuranceResponsibility) {
    disabledByList.push(
      `Set insurance responsibility for line items you want to upload on claim`
    );
  }

  if (noInsurancePolicies) {
    disabledByList.push(`Add an insurance policy to patient chart`);
  }

  const isDisabled = disabledByList.length > 0;

  return {
    disabled: isDisabled,
    tooltip: isDisabled ? (
      <Box>
        <Typography>To file an insurance claim</Typography>
        <List>
          {disabledByList.map((reason, index) => (
            <ListItem key={reason}>{`${index + 1}) ${reason}`}</ListItem>
          ))}
        </List>
      </Box>
    ) : undefined
  };
};

export const getPaymentMenuProps = (params: {
  isInsurancePaymentsAndAdjustmentsEnabled: boolean;
  navigate: NavigateFunction;
  originPathname: string;
  saleId: number;
  patientId: number;
  newWindow?: boolean;
  onPostPayment?: () => void;
}) => {
  const {
    isInsurancePaymentsAndAdjustmentsEnabled,
    navigate,
    originPathname,
    saleId,
    patientId,
    newWindow,
    onPostPayment
  } = params;

  return {
    label: isInsurancePaymentsAndAdjustmentsEnabled
      ? "Patient Payment"
      : "Post Payment",
    onClick: () => {
      if (onPostPayment) {
        onPostPayment();
      } else if (newWindow) {
        window.open(
          `/patients/${patientId}/billing/${saleId}/payment`,
          "_blank",
          "noopener,noreferrer"
        );
      } else {
        navigate(`/patients/${patientId}/billing/${saleId}/payment`, {
          state: { originPathname }
        });
      }
    }
  };
};

export const getOnlinePaymentMenuProps = (params: {
  isPaymentsRequestsEnabled: boolean;
  hasPaymentProvider: boolean;
  isInsurancePaymentsAndAdjustmentsEnabled: boolean;
  practiceBillingSettings?: PracticeBillingSettings;
  navigate: NavigateFunction;
  originPathname: string;
  saleId: number;
  patientId: number;
  newWindow?: boolean;
}) => {
  const {
    isPaymentsRequestsEnabled,
    hasPaymentProvider,
    isInsurancePaymentsAndAdjustmentsEnabled,
    practiceBillingSettings,
    navigate,
    originPathname,
    saleId,
    patientId,
    newWindow
  } = params;

  return {
    shouldRender:
      isPaymentsRequestsEnabled &&
      hasPaymentProvider &&
      practiceBillingSettings?.online_payment_enabled,
    label: isInsurancePaymentsAndAdjustmentsEnabled
      ? "Patient Online Payment Request"
      : "Online Payment Request",
    tooltip: "Create an Online Payment Request for the Sale",
    onClick: () => {
      if (newWindow) {
        window.open(
          `/patients/${patientId}/billing/${saleId}/payment-request`,
          "_blank",
          "noopener,noreferrer"
        );
      } else {
        navigate(`/patients/${patientId}/billing/${saleId}/payment-request`, {
          state: { originPathname }
        });
      }
    }
  };
};

export const hasClinicPaymentProvider = (currentClinic: Clinic) => {
  // Gravity is enabled per-clinic via the provider enum (clinic value wins, else the practice's);
  // the gravity_account_id is validated server-side (fail-loud), so it isn't fetched here.
  // Keeps this UI gate aligned with the processor gate in use-credit-card.hook.
  if (resolveCardProcessingProvider(currentClinic) === GRAVITY_PAYMENT_METHOD) {
    return true;
  }

  const practiceCardProcessingProviderAccountId =
    currentClinic?.practice?.card_processing_provider === TILLED_PAYMENT_METHOD
      ? currentClinic?.practice?.tilled_account_id
      : currentClinic?.practice?.justifi_account_id;

  const clinicCardProcessingProviderAccountId =
    currentClinic?.card_processing_provider === TILLED_PAYMENT_METHOD
      ? currentClinic?.tilledAccountId
      : currentClinic?.justifiAccountId;

  const cardProcessingProviderAccountId =
    clinicCardProcessingProviderAccountId ||
    practiceCardProcessingProviderAccountId;

  return Boolean(cardProcessingProviderAccountId);
};

export const getSaleAdpFormFileName = (params: {
  patientFirstName?: string;
  patientLastName?: string;
  saleServiceDate?: string | null;
}) => {
  const { patientFirstName, patientLastName, saleServiceDate } = params;
  return `${patientFirstName}_${patientLastName}_${
    saleServiceDate
      ? `sale_${formatBackendDateOnly(saleServiceDate, "MM/dd/yyyy")}_`
      : ""
  }adp_form_data.xml`;
};

export const DELETE_INVOICE_CONFIRM_TITLE = "Delete this invoice permanently?";

export const DELETE_INVOICE_CONFIRM_OPTIONS: ConfirmDialogOptions = {
  actionButtonType: "danger",
  actionButtonText: "Delete invoice"
};

export const DELETE_INVOICE_CONFIRM_CONTENT = (
  <Stack gap={1}>
    <Typography
      data-testid="delete-invoice-irreversible"
      variant="body3"
      sx={{ color: error[700], fontWeight: 700 }}
    >
      This cannot be undone. Suno Support cannot recover a deleted invoice.
    </Typography>
    <Typography variant="body3">
      Its line items, payments, and adjustments will be deleted with it.
    </Typography>
  </Stack>
);
